import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Shield, FileText, Search, ShoppingBag, Building2, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

export const Services = () => {
  const [ref1, isVisible1] = useScrollAnimation();
  const [ref2, isVisible2] = useScrollAnimation();
  const [ref3, isVisible3] = useScrollAnimation();

  const services = [
    {
      id: 1,
      title: 'Trademark Registration',
      icon: Shield,
      description: 'Protect your brand with comprehensive trademark registration services. Secure exclusive rights to your business name and logo.',
      features: ['Comprehensive Search', 'USPTO Filing', 'Legal Protection', 'Expert Guidance'],
      link: '/services/trademark-registration',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      id: 2,
      title: 'Copyright Registration',
      icon: FileText,
      description: 'Safeguard your creative works including books, music, art, and software with official copyright registration.',
      features: ['Creative Works', 'Official Registration', 'Legal Evidence', 'Nationwide Protection'],
      link: '/services/copyright-registration',
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-50'
    },
    {
      id: 3,
      title: 'Trademark Search',
      icon: Search,
      description: 'Comprehensive trademark searches to identify potential conflicts before filing and protect your investment.',
      features: ['Database Search', 'Conflict Analysis', 'Risk Assessment', 'Detailed Report'],
      link: '/services/trademark-search',
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 4,
      title: 'Company Formation',
      icon: Building2,
      description: 'Professional business formation services for LLCs, corporations, and other business entities with expert guidance.',
      features: ['LLC Formation', 'Corporation Setup', 'Compliance Support', 'Fast Processing'],
      link: '/services/company-formation',
      color: 'from-slate-600 to-slate-700',
      bgColor: 'bg-slate-50'
    },
    {
      id: 5,
      title: 'Amazon Brand Registry',
      icon: ShoppingBag,
      description: 'Protect your Amazon listings and enhance your brand presence with comprehensive Brand Registry enrollment.',
      features: ['Listing Protection', 'Counterfeit Prevention', 'Enhanced Tools', 'Brand Analytics'],
      link: '/services/amazon-brand-registry',
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50'
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Our Services | Comprehensive Trademark & Business Services</title>
        <meta name="description" content="Explore our comprehensive trademark registration, copyright protection, business formation, and brand protection services. Expert legal guidance for all your intellectual property needs." />
        <meta name="keywords" content="trademark services, copyright services, business formation, brand protection, intellectual property services" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
              <span className="text-sm font-semibold">🎯 Professional Services</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">Our Services</h1>
            <p className="text-xl leading-relaxed max-w-3xl mx-auto">
              Comprehensive trademark, copyright, and business formation services to protect and grow your brand
            </p>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={service.id}
                    ref={index === 0 ? ref1 : index === 2 ? ref2 : index === 4 ? ref3 : null}
                    className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-primary/20 ${
                      (index === 0 && isVisible1) || (index === 2 && isVisible2) || (index === 4 && isVisible3) ? 'animate-slide-in' : ''
                    }`}
                  >
                    {/* Icon */}
                    <div className={`${service.bgColor} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`bg-gradient-to-r ${service.color} bg-clip-text text-transparent`} size={32} strokeWidth={2.5} />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                    <p className="text-base text-gray-600 leading-relaxed mb-6">{service.description}</p>

                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Link */}
                    <Link to={service.link}>
                      <Button className="w-full bg-primary hover:bg-primary/90 h-11 text-base font-semibold group-hover:shadow-lg transition-all">
                        Learn More
                        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose Our Services?</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We provide comprehensive protection for your intellectual property with expert guidance every step of the way
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="text-primary" size={40} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Guidance</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Professional assistance from experienced trademark and intellectual property specialists
                </p>
              </div>

              <div className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-primary" size={40} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Processing</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Quick turnaround times with efficient filing and registration processes
                </p>
              </div>

              <div className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="text-primary" size={40} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Protection</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Full-service solutions covering all aspects of brand and intellectual property protection
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Protect Your Brand?</h2>
            <p className="text-xl mb-8 text-white/95">
              Get started with our professional services today and secure your business identity
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-white text-primary hover:bg-gray-100 h-12 px-8 text-base font-semibold">
                  Get Started Today
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 h-12 px-8 text-base font-semibold">
                  Live Chat
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
